import MainSquareButton from '../../ui/buttons/MainSquareButton';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import {
  cartSVG,
  clockSVG,
  linkSVG,
  pulseSVG,
  settingSVG,
  shopifySVG,
  tickWithCircleSVG,
  warningSVG,
} from '../../lib/allCustomSVGs';
import { useTranslation } from 'react-i18next';
import StatusCard from '../../ui/cards/StatusCard';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../store/store';
import { setAiOnboardingSelectedIntegrate } from '../../store/reducers/userSlice';
import '../../styles/pages/quickOnboarding/quickOnboardingIntegrations.scss';
import { CodeSection } from 'react-code-section-lib';
import { shopifyAdminURL } from '../../lib/constants/central-api';
import {
  regularScriptCode,
  shopifyPixelCode,
  shopifyPixelName,
} from '../../lib/constants/integration_script_code';
import googleAnalyticsButtonImg from '../../assets/GA.png';
import post_google_analytics_custom_dimensions_api_call from '../../lib/api/integration/post_google_analytics_custom_dimensions_api_call';
import { useEffect, useState } from 'react';
import get_google_analytics_custom_dimensions_api_call from '../../lib/api/integration/get_google_analytics_custom_dimensions_api_call';
import get_all_available_mcp_api_call from '../../lib/api/mcp/get-all-available-mcp';
import { setMessage } from '../../store/reducers/appSlice';

type Props = {
  isTrackingScriptConnected: boolean;
  isLoadingConnection: boolean;
  checkConnection: any;
  apiKey: string;
  isGoogleAnalyticsConnected: boolean;
  setIsGoogleAnalyticsConnected: (connected: boolean) => void;
};

const AIOptimizationsStatusCard = ({
  isTrackingScriptConnected,
  isLoadingConnection,
  checkConnection,
  apiKey,
  isGoogleAnalyticsConnected,
  setIsGoogleAnalyticsConnected,
}: Props) => {
  const { t } = useTranslation();
  const aiTrackerIntegrationAge = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_integration_age
  );
  const selected_integrate = useSelector(
    (state: RootState) =>
      state.user?.ai_onboarding?.selected_integrate || 'standard'
  );

  const [isGoogleAnalyticsChecking, setIsGoogleAnalyticsChecking] =
    useState(false);
  const [isGoogleAnalyticsConnecting, setIsGoogleAnalyticsConnecting] =
    useState(false);

  const statusCardsData = [
    {
      title: t('IntegrationAge'),
      info: aiTrackerIntegrationAge,
      description: t('SinceFirstIntegration'),
      icon: clockSVG,
    },
    {
      title: t('Status'),
      info: isTrackingScriptConnected ? t('Active') : t('Inactive'),
      description: isTrackingScriptConnected
        ? t('IntegrationIsWorkingProperly')
        : t('IntegrationPending'),
      icon: isTrackingScriptConnected ? tickWithCircleSVG : warningSVG,
      color: isTrackingScriptConnected ? 'green' : 'red',
    },
  ];

  const checkGoogleAnalyticsCustomDimensions = async () => {
    setIsGoogleAnalyticsChecking(true);
    const isConnected = await get_google_analytics_custom_dimensions_api_call();
    setIsGoogleAnalyticsConnected(isConnected);
    setIsGoogleAnalyticsChecking(false);
  };

  useEffect(() => {
    if (!isGoogleAnalyticsConnected) {
      checkGoogleAnalyticsCustomDimensions();
    }
  }, [isGoogleAnalyticsConnected]);

  const handleGoogleAnalyticsConnection = async () => {
    setIsGoogleAnalyticsConnecting(true);
    const response = await post_google_analytics_custom_dimensions_api_call();

    if (!response) {
      //! Need to call this since we need the GA MCP info, better if we had a separate API to fetch a specific MCP only
      const allMCPs = await get_all_available_mcp_api_call();
      const gaMCP = allMCPs.find(
        (item: any) => item.slug === 'google_analytics'
      );

      // UTF-8-safe: btoa() alone throws on characters outside Latin-1
      // (mirrors constructDynamicOauthURL in MCP.tsx, which also decodes it).
      const STATE = btoa(
        unescape(
          encodeURIComponent(
            JSON.stringify({
              ...gaMCP,
              isAIOptimizeRedirection: true,
            })
          )
        )
      );
      const REDIRECT_URI = `${window.location.origin}/integrations`;

      const URL = `${gaMCP?.oauth_url}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;

      //? Redirect to the Google Analytics OAuth consent screen
      window.location.href = URL;
    } else {
      setIsGoogleAnalyticsConnecting(false);
      setIsGoogleAnalyticsConnected(true);
      store.dispatch(
        setMessage({
          message: t('ConnectionSuccessfulGA'),
          messageType: 'success',
        })
      );
    }
  };

  const renderCode = () => {
    if (selected_integrate === 'standard' && apiKey) {
      return (
        <CodeSection showLineNumbers theme={'dark'} lang={'html'}>
          {regularScriptCode(apiKey)}
        </CodeSection>
      );
    }
    if (selected_integrate === 'shopify' && apiKey) {
      return (
        <CodeSection showLineNumbers theme={'dark'} lang={'js'}>
          {shopifyPixelCode(apiKey)}
        </CodeSection>
      );
    }
  };

  const renderCodeInstructions = () => {
    if (selected_integrate === 'standard') {
      return (
        <>
          <p>{t('CheckoutTrackingIntegrationInstructions1')}</p>
          <ul>
            <li>
              {t('CheckoutTrackingIntegrationInstructions2')}{' '}
              <strong>{`<head>`}</strong>
            </li>
          </ul>
        </>
      );
    } else {
      return (
        <>
          <p
            style={{
              fontSize: '1.8rem',
              fontWeight: '800',
              marginBottom: '2rem',
            }}
          >
            {/* {t(
              APP_ENV !== 'LIVE'
                ? 'CheckoutTrackingIntegrationInstructions0-Shopify-New'
                : 'CheckoutTrackingIntegrationInstructions0-Shopify'
            )} */}
            {/* {t('CheckoutTrackingIntegrationInstructions0-Shopify')} */}
          </p>

          {/* {APP_ENV !== 'LIVE' && (
            <>
              <p>{t('CheckoutTrackingIntegrationInstructions8-Shopify')}</p>
              <div
                className='normalForm__container1--left'
                style={{ marginBottom: '1.8rem', width: '33.8rem' }}
              >
                <div className='login__right--other'>
                  <div className='login__right--bottom-button'>
                    <MainSquareButton
                      value={
                        isInstalling
                          ? `${t('Installing')}...`
                          : `${t(
                              'CheckoutTrackingIntegrationInstructions9-Shopify'
                            )}`
                      }
                      image={shopifyButtonImg}
                      type='button'
                      disabled={isInstalling || loading !== 'success'}
                      onClick={() => {
                        setIsInstalling(true);
                        window.location.href = SHOPIFY_AUTH_URL(
                          webshop_url,
                          `${window.location.origin}/onboarding`
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )} */}

          <p>{t('CheckoutTrackingIntegrationInstructions1-Shopify')}</p>
          <ul>
            <li>{t('CheckoutTrackingIntegrationInstructions2-Shopify')}</li>({' '}
            <a
              onClick={() => {
                window.open(shopifyAdminURL, '_blank');
              }}
            >
              {shopifyAdminURL}
            </a>{' '}
            )<li>{t('CheckoutTrackingIntegrationInstructions3-Shopify')}</li>
            <li>{t('CheckoutTrackingIntegrationInstructions4-Shopify')}</li>
            <li>{t('CheckoutTrackingIntegrationInstructions5-Shopify')}</li>
            <div
              className='code__snippet__single-line'
              style={{
                width: '25rem',
                marginTop: '-4rem',
                marginBottom: '-1rem',
                marginLeft: '1.5rem',
              }}
            >
              <CodeSection showLineNumbers={false} theme={'dark'} lang={'html'}>
                {shopifyPixelName(apiKey)}
              </CodeSection>
            </div>
            <li>{t('CheckoutTrackingIntegrationInstructions6-Shopify')}</li>
            <li>{t('CheckoutTrackingIntegrationInstructions7-Shopify')}</li>
          </ul>
        </>
      );
    }
  };

  return (
    <div className='aiOptimizationsStatusCard'>
      <div className='aiOptimizationsStatusCard__title'>
        <CustomSVGs svg={pulseSVG} />
        <h3>{t('IntegrationStatus')}</h3>
      </div>
      <div className='aiOptimizationsStatusCard__message'>
        {t('IntegrationStatus-msg')}
      </div>

      <div className='aiOptimizationsStatusCard__status'>
        {statusCardsData?.map((card, index) => (
          <StatusCard
            key={index}
            title={card?.title}
            info={card?.info}
            description={card?.description}
            icon={card?.icon}
            color={card?.color}
          />
        ))}
      </div>

      <div className='aiOptimizationsStatusCard__buttons'>
        <MainSquareButton
          value={isLoadingConnection ? t('Checking') : t('CheckConnection')}
          type='button'
          disabled={isLoadingConnection}
          icon={linkSVG}
          onClick={checkConnection}
        />
      </div>

      {/* Integration Setup Title */}
      <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <div className='aiOptimizationsStatusCard__title'>
          <CustomSVGs svg={settingSVG} />
          <h3>{t('IntegrationSetup')}</h3>
        </div>
        <div className='aiOptimizationsStatusCard__message'>
          {t('FollowTheseStepsToIntegrateTrackingOnYourWebsite')}
        </div>
      </div>

      {/* cards section */}
      <div className='quickOnboardingIntegrations__cards'>
        <div
          onClick={() =>
            store.dispatch(setAiOnboardingSelectedIntegrate('standard'))
          }
          data-is-card-selected={
            selected_integrate === 'standard' ? 'true' : 'false'
          }
          className='quickOnboardingIntegrations__cards--card'
        >
          <div className='quickOnboardingIntegrations__cards--card-icon'>
            <CustomSVGs svg={cartSVG} />
          </div>
          <div className='quickOnboardingIntegrations__cards--card-text'>
            <p>{t('StandardScriptIntegration')}</p>
            <span>{t('StandardScriptIntegration-info')}</span>
          </div>
        </div>
        <div
          onClick={() =>
            store.dispatch(setAiOnboardingSelectedIntegrate('shopify'))
          }
          data-is-card-selected={
            selected_integrate === 'shopify' ? 'true' : 'false'
          }
          className='quickOnboardingIntegrations__cards--card'
        >
          <div className='quickOnboardingIntegrations__cards--card-icon'>
            <CustomSVGs svg={shopifySVG} />
          </div>
          <div className='quickOnboardingIntegrations__cards--card-text'>
            <p>{t('ShopifyWebPixelIntegration')}</p>
            <span>{t('ShopifyWebPixelIntegration-info')}</span>
          </div>
        </div>
      </div>

      {/* Google Analytics Connection section */}
      <div className='quickOnboardingIntegrations__code--titles'>
        <p>{t('CheckoutTrackingIntegrationInstructionsGA1')}</p>
        <ul>
          <li>{t('CheckoutTrackingIntegrationInstructionsGA2')}</li>
        </ul>
        <div
          className='normalForm__container1--left'
          style={{
            marginTop: '-1rem',
            marginBottom: '1.8rem',
            maxWidth: '32.3rem',
          }}
        >
          {/* <div className='login__right--other'>
            <div className='login__right--bottom-button'>
              <MainSquareButton
                value={
                  isGoogleAnalyticsConnecting
                    ? `${t('Connecting')}...`
                    : isGoogleAnalyticsChecking
                    ? `${t('VerifyingGAConnection')}...`
                    : isGoogleAnalyticsConnected
                    ? `${t('ConnectionSuccessfulGA')}`
                    : `${t('CheckoutTrackingIntegrationInstructionsGA3')}`
                }
                image={googleAnalyticsButtonImg}
                type='button'
                disabled={
                  isGoogleAnalyticsConnecting ||
                  isGoogleAnalyticsChecking ||
                  isGoogleAnalyticsConnected
                }
                onClick={handleGoogleAnalyticsConnection}
              />
            </div>
          </div> */}
          <div className='aiOptimizationsStatusCard__ga-connection login__right--other'>
            <div className='login__right--bottom-button'>
              <MainSquareButton
                value={
                  isGoogleAnalyticsConnecting
                    ? `${t('Connecting')}...`
                    : isGoogleAnalyticsChecking
                    ? `${t('VerifyingGAConnection')}...`
                    : isGoogleAnalyticsConnected
                    ? `${t('ConnectionSuccessfulGA')}`
                    : `${t('CheckoutTrackingIntegrationInstructionsGA4')}`
                }
                image={googleAnalyticsButtonImg}
                contentLayout='imageTextSeparate'
                type='button'
                disabled={
                  isGoogleAnalyticsChecking || isGoogleAnalyticsConnected
                }
                onClick={() => {
                  window.open(
                    'https://intercom.help/ad-up/en/articles/12095225-adup-create-custom-dimensions',
                    '_blank'
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Code section */}
      <div className='quickOnboardingIntegrations__code'>
        <div className='quickOnboardingIntegrations__code--titles'>
          {renderCodeInstructions()}
        </div>
        <div className='quickOnboardingIntegrations__code--code'>
          {renderCode()}
        </div>
      </div>
    </div>
  );
};

export default AIOptimizationsStatusCard;
